import { useFormContext } from 'react-hook-form'
import { IndividualFindArtFormData } from '../../types'
import { COLOR_TYPES } from './ColorStep'
import { abbreviateNumber } from 'js-abbreviation-number'
import { MAX_BUDGET } from 'views/find-art/config'
import { useEffect, useRef } from 'react'
import cn from 'classnames'

const formatNumber = (number: number) => {
  return abbreviateNumber(number, 1)
}

type FormDataType = IndividualFindArtFormData

type Props = {
  displayProps: Array<keyof FormDataType>
  classNames?: string
}

const formatArrayOptions = (options: string[]) => {
  if (options.length === 0) return ''
  if (options.length === 1) return options[0]
  if (options.length === 2) return options.join(' and ')

  return `${options.slice(0, -1).join(', ')}, and ${options.slice(-1)[0]}`
}

const PreviewText = (props: Props) => {
  const { displayProps, classNames } = props
  const previewRef = useRef<HTMLSpanElement>(null)
  const { watch } = useFormContext<FormDataType>()

  useEffect(() => {
    if (previewRef.current) {
      const content = previewRef.current.innerHTML

      if (content.length > 0) {
        // Find the first non-whitespace character
        const match = content.match(/(<[^>]+>|[^<\s])/) // Regex to find the first character or tag

        if (match && match.index !== undefined && match[0] !== undefined) {
          const index = match.index + match[0].length
          const firstChar = content[index]
          const upperFirstChar = firstChar.toUpperCase()
          // Replace the first character with its uppercase version
          const newContent = content.slice(0, index) + upperFirstChar + content.slice(index + 1)
          previewRef.current.innerHTML = newContent
        }
      }
    }
  }, [])

  const previewText = (() => {
    // Budget
    const budget = displayProps.includes('budget') ? watch('budget') : []
    const customBudget = displayProps.includes('budget') ? watch('customBudget') : { min: null, max: null }

    let budgetText = ''
    const filteredBudget = budget.filter((budgetOption) => budgetOption.isSelected)
    filteredBudget?.forEach((budgetOption, index) => {
      const { isOther } = budgetOption

      const min = isOther ? customBudget.min : budgetOption.min
      const max = isOther ? customBudget.max : budgetOption.max
      if (max === MAX_BUDGET) {
        budgetText += min ? `$${formatNumber(min)}+` : ''
      } else if (min && max) {
        budgetText += `$${formatNumber(min)}-$${formatNumber(max)}`
      } else if (min && !max) {
        budgetText = `Minimum $${formatNumber(min)}`
      } else if (!min && max) {
        budgetText += `Maximum $${formatNumber(max)}`
      }
      if (index !== filteredBudget.length - 1) budgetText += ' & '
    })

    // Color
    const artColors = displayProps.includes('artColors') && (watch('artColors') || [])
    const artColorsTextArray = artColors
      ? artColors?.map((color) => COLOR_TYPES.find((item) => item.value === color)?.label || '')
      : []

    // Art Sizes
    const artSize = displayProps.includes('size') ? watch('size') : []
    let artSizeText = ''
    const filteredArtSize = artSize.filter((size) => size.isSelected)
    filteredArtSize?.forEach((size, index) => {
      const sizeLabel = size.label.substring(0, size.label.indexOf('(')).trim()
      artSizeText += sizeLabel
      if (index !== filteredArtSize.length - 1) artSizeText += ' & '
    })

    // Room Type
    const roomType = displayProps.includes('roomType') && watch('roomType')

    const artStyles = displayProps.includes('artStyles') && (watch('artStyles') || [])

    const result = (
      <span ref={previewRef}>
        {budgetText && (
          <span>
            <strong>{`${budgetText.toLowerCase()}`}&nbsp;</strong>
          </span>
        )}
        {artSizeText && <strong>{artSizeText.toLowerCase()}&nbsp;</strong>}
        {artColorsTextArray.length > 0 && <strong>{formatArrayOptions(artColorsTextArray).toLowerCase()}&nbsp;</strong>}
        <span>art for my&nbsp;</span>
        {roomType && <strong>{roomType.toLowerCase()}&nbsp;</strong>}
        {artStyles && (
          <>
            with&nbsp;
            <strong>{formatArrayOptions(artStyles)}</strong>
            {artStyles.length === 1 ? ' style' : ' styles'}&nbsp;
          </>
        )}
      </span>
    )

    return result
  })()

  return (
    <div
      className={cn(
        'mx-4 mb-2 w-fit rounded-lg  px-4 py-2 text-center text-[16px]',
        'font-medium leading-[1.4] text-black lg:mx-10 lg:mb-4 lg:text-[24px]',
        'border border-black/10  shadow-lg',
        classNames,
      )}
    >
      {previewText}
    </div>
  )
}

export default PreviewText
